import {
  Cookie,
  CookieCategory,
  OneTrustCookie,
  OneTrustDomainData,
  OneTrustGroup,
} from '../types/cookieTypes';

// Extend the global Window interface to include OneTrust
declare global {
  interface Window {
    OneTrust?: {
      GetDomainData(): OneTrustDomainData;
    };
  }
}

const transformOneTrustCookie = (cookie: OneTrustCookie): Cookie => {
  return {
    name: cookie.Name,
    host: cookie.Host,
    description: cookie.description,
    expiry: cookie.Length,
    session: cookie.IsSession,
  };
};

const mapOneTrustGroups = (groups: OneTrustGroup[]): CookieCategory[] => {
  const categories: CookieCategory[] = [];

  groups.forEach((group) => {
    const cookies: Cookie[] = [];

    group.Cookies.forEach((cookie) => {
      cookies.push(transformOneTrustCookie(cookie));
    });

    categories.push({
      optanonId: group.OptanonGroupId,
      name: group.GroupName,
      description: group.GroupDescription,
      optanonParentId: group.Parent,
      cookies,
    });
  });

  return categories;
};

export class OneTrustJSApiService {
  private readonly INITIAL_DELAY_MS = 300;
  private readonly MAX_DELAY_MS = 1000;
  private readonly MAX_RETRY_ATTEMPTS = 5;

  static isOneTrustAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!window.OneTrust &&
      typeof window.OneTrust.GetDomainData === 'function'
    );
  }

  private async waitForOneTrust(): Promise<void> {
    let attempts = 0;
    let delay = this.INITIAL_DELAY_MS;

    while (attempts < this.MAX_RETRY_ATTEMPTS) {
      if (OneTrustJSApiService.isOneTrustAvailable()) {
        return;
      }
      attempts += 1;
      const currentDelay = delay;
      await new Promise((resolve) => setTimeout(resolve, currentDelay));
      delay = Math.min(delay * 2, this.MAX_DELAY_MS);
    }

    throw new Error(
      `OneTrust SDK not available after ${this.MAX_RETRY_ATTEMPTS} attempts. Please ensure OneTrust is properly loaded.`
    );
  }

  async fetchCookieData(): Promise<CookieCategory[]> {
    await this.waitForOneTrust();

    const domainData = window.OneTrust!.GetDomainData();

    if (!domainData || !domainData.Groups) {
      throw new Error('No cookie groups found in OneTrust domain data.');
    }

    return mapOneTrustGroups(domainData.Groups);
  }
}

export const oneTrustApi = new OneTrustJSApiService();
